import { useReferralDispatch, useReferralStore } from '@/components/Referral/ReferralProvider';
import { copyText } from '@/components/Referral/util';
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Input';
import { message } from '@/components/UI/Message';
import EfModal from '@/components/UI/Modal';
import { Cash, Coupon, EcoCredits, EcoCreditsBlue, FaceBookIconNew, GrandPrizes, GrandPrizesBlue, TwitterIconNew } from '@/components/UI/svg/Referral';
import { sendInviteEmail } from '@/service/referral';
import { loginUser } from '@/utils/cookie';
import { signupCookie } from '@/utils/cookies/signupCookie';
import gtm from '@/utils/gtm';
import efBridge from '@/utils/JSBridge';
import { useGlobal } from '@/utils/useGlobal';
import EfLink from 'elem/ui/ef-link';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { PlanType } from '../../index.d';
import styled from './index.module.scss';

export type ReferHandle = {
    show: () => void;
};
interface ReferPorps {
    isShare?: boolean;
    hide?: boolean;
}
enum TAB_TYPE {
    Referrer,
    Referee,
}
const reg = /^[A-Za-z\d]+([.%~`$^*&(){}<>?"+=!_\\-]*[A-Za-z\d-])*@[a-zA-Z\d-]{1,30}\.[a-zA-Z\d.%#~`$^*&(){}<>?"+=!_\\-]{1,30}$/;

const delCouponLocale = ['us', 'ca', 'kr'];

/**
 * @description: According different site return specific copy
 */
const copyTransfer = (locale: string, type: string | undefined, t: any, switchKey = true) => {
    const basicCopy = {
        tab1_item3_desc: t('refer.tab1_item3_desc'),
        tab1_item3_title: t('refer.tab1_item3_title'),
        tab2_item1_desc: t('refer.tab2_item1_desc'),
        tab2_item2_desc: t('refer.tab2_item2_desc'),
    };

    if (type === PlanType.CAMPAIGN && switchKey) {
        if (locale === 'us' || locale === 'ca') {
            return {
                ...basicCopy,
                ...{
                    tab1_item3_desc:
                        'Get 10% cash back when your referred-in friend makes a first purchase within 30 days. The first order value must exceed $299.',
                    tab2_item1_desc: 'New users can earn up to 2000 EcoCredits for their first registration',
                },
            };
        } else if (locale === 'ca') {
            return {
                ...basicCopy,
                ...{
                    tab1_item3_desc:
                        'Get 10% cash back when your referred-in friend makes a first purchase within 30 days. The first order value must exceed $299.',
                },
            };
        } else if (locale === 'kr') {
            return {
                ...basicCopy,
                ...{
                    tab1_item3_desc: '초대한 친구가 회원 가입 후 30일 이내 첫 주문 시 (30만원 이상), 해당 금액의 5% 캐시백을 "추천인에게" 증정',
                    tab1_item3_title: '캐시백',
                    tab2_item2_desc: '추천하는 친구가 다음 주문건에\n 적용 가능한 2% 할인쿠폰을\n 받을 수 있습니다',
                },
            };
        } else if (locale === 'za') {
            return {
                ...basicCopy,
                ...{
                    tab1_item3_desc:
                        'Get 8% cash back when your referred-in friend makes a first purchase within 30 days. The first order value must exceed R5000.',
                },
            };
        }
    }

    return basicCopy;
};

const Refer = ({ isShare = true, hide = false }: ReferPorps, ref: ForwardedRef<ReferHandle>) => {
    const { useLang, useRouter, Link, isMobile, isApp } = useGlobal();
    const router = useRouter();
    const { locale, asPath } = useRouter();
    const { t } = useLang('referral');
    const { t: sharedT } = useLang('shared');
    const [show, setShow] = useState(false);
    const { shareInfo, referralInfo, inBlackList, actStatus, pageUsing, isLogin } = useReferralStore();
    const dispatch = useReferralDispatch();
    const [copyValue, setCopyValue] = useState('');
    const [sendEmail, setSendEmail] = useState('');
    const [selectTab, setSelectTab] = useState(TAB_TYPE.Referrer);

    const item1_copy = actStatus === 1 ? t('refer.tab1_item1_desc_1') : t('refer.tab1_item1_desc');
    // 分享文案
    const shareText = {
        faceBook: `${t('refer.share_text')}\n${copyValue}`,
        twitter: `${t('refer.share_text')}\n${copyValue}`,
    };

    const intl = copyTransfer(locale, referralInfo?.planType, t, !(pageUsing === 'dp3'));

    const handleShareInfo = () => {
        efBridge.callHandler('shareMessage', {
            title: t('refer.share_text'),
            url: copyValue,
        });
    };

    const handleShare = async () => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: t('refer.title'),
                entry_position: 1,
            });
            location.href = `/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}?pop=share`)}`;
            return;
        }
        if (!isShare) {
            router.push(pageUsing === 'd3ms' ? '/referral-delta-3-max-series' : '/referral');
            return;
        }

        let shareUrlPath = 'referral-rewards';

        if (pageUsing === 'dp3') {
            shareUrlPath = 'referral-rewards-delta-pro-3-portable-power-station';
        }

        if (pageUsing === 'd3ms') {
            shareUrlPath = 'referee-delta-3-max-series';
        }
        // const fp = await FingerprintJS.load();
        // const { visitorId } = await fp.get();
        dispatch({
            type: 'getShareInfo',
            payload: {
                data: {
                    planId: referralInfo?.planId,
                    shareUrl: `${location.origin}/${locale}/${shareUrlPath}`,
                },
                // deviceId: visitorId,
                locale,
            },
        });
        setShow(true);
    };

    const handleSendMsg = () => {
        if (!referralInfo || !shareInfo) return;
        const { email } = loginUser().user;
        // 如果与用户邮箱相同提示
        if (email && email === sendEmail.trim()) return message.error(t('refer.thesameemail'));
        sendInviteEmail(
            {
                planId: referralInfo?.planId,
                shareEmail: sendEmail.trim(),
                shareId: shareInfo?.shareId,
                shareUrl: copyValue,
            },
            locale,
        )
            .then((res) => {
                if (res?.code !== '0') return message.error(res?.message);
                message.success(sharedT('my.site_account_profile_save_success'));
                setSendEmail('');
            })
            .catch((err) => {
                console.log(err);
            });
    };

    useEffect(() => {
        if (!shareInfo) return;
        let path = 'referral-rewards';
        if (pageUsing === 'dp3') path = 'referral-rewards-delta-pro-3-portable-power-station';
        if (pageUsing === 'd3ms') path = 'referee-delta-3-max-series';
        setCopyValue(`${location.origin}/${locale}/${path}?inviteCode=${shareInfo?.inviteCode}`);
    }, [shareInfo]);

    useImperativeHandle(ref, () => ({
        show: () => handleShare(),
    }));

    if (!referralInfo) return <></>;

    return (
        <div className={styled.refer} style={{ display: hide ? 'none' : 'block' }}>
            <h3 className="title">{t('refer.title')}</h3>
            <div className="tabs">
                <p className={`tab ${selectTab === TAB_TYPE.Referrer ? 'active' : ''}`} onClick={() => setSelectTab(TAB_TYPE.Referrer)}>
                    {t('refer.tab1_title')}
                </p>
                <p className={`tab ${selectTab === TAB_TYPE.Referee ? 'active' : ''}`} onClick={() => setSelectTab(TAB_TYPE.Referee)}>
                    {t('refer.tab2_title')}
                </p>
            </div>
            <div className={`tab-wrap ${selectTab === TAB_TYPE.Referrer ? 'show' : ''}`}>
                <p className="tab-desc">{t('refer.tab1_desc')}</p>
                <div className="item-wrap">
                    <div className="item">
                        <div className="icon">
                            <EcoCredits />
                        </div>
                        <div className="text">
                            <p className="item-title">{t('refer.tab1_item1_title')}</p>
                            <p className="item-desc">{item1_copy}</p>
                        </div>
                    </div>
                    <div className="item">
                        <div className="icon">
                            <div className="icon">{locale === 'za' ? <Coupon /> : <Cash />}</div>
                        </div>
                        <div className="text">
                            <p className="item-title">{t('refer.tab1_item3_title')}</p>
                            <p className="item-desc">{intl.tab1_item3_desc}</p>
                        </div>
                    </div>
                    {pageUsing !== 'dp3' && (
                        <div className="item">
                            <div className="icon">
                                <GrandPrizes />
                            </div>
                            <div className="text">
                                <p className="item-title">{t('refer.tab1_item4_title')}</p>
                                <p className="item-desc">{t('refer.tab1_item4_desc')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={`tab-wrap tab2-wrap ${selectTab === TAB_TYPE.Referee ? 'show' : ''}`}>
                <p className="tab-desc">{t('refer.tab2_desc')}</p>
                <div className="item-wrap">
                    <div className="item">
                        <div className="icon">
                            <EcoCreditsBlue />
                        </div>
                        <div className="text">
                            <p className="item-title">{t('refer.tab2_item1_title')}</p>
                            <p className="item-desc">{intl.tab2_item1_desc}</p>
                        </div>
                    </div>
                    <div className="item">
                        <div className="icon">
                            <Coupon />
                        </div>
                        <div className="text">
                            <p className="item-title">{t('refer.tab2_item2_title')}</p>
                            <p className="item-desc">{intl.tab2_item2_desc}</p>
                        </div>
                    </div>
                    {!delCouponLocale.includes(locale) && (
                        <div className="item">
                            <div className="icon">
                                <GrandPrizesBlue />
                            </div>
                            <div className="text">
                                <p className="item-title">{t('refer.tab2_item3_title')}</p>
                                <p className="item-desc">{t('refer.tab2_item3_desc')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Button className="refer-btn" onClick={handleShare} disabled={inBlackList}>
                {t('refer.btn1')}
            </Button>
            <EfModal
                width={560}
                visible={show}
                open={show}
                className={`${styled.refer_modal}`}
                onCancel={() => setShow(false)}
                getContainer={true}
                maskClosable={isMobile}
                centered={!isMobile || isApp}
            >
                <div className={styled.refer_modal}>
                    <section className="refer-title">
                        <h5 className="title">{t('refer.pop_refer_title')}</h5>
                        <p className="desc">{t('refer.pop_refer_desc')}</p>
                    </section>
                    <section className="share-link">
                        <h5 className="title">{t('refer.pop_share_link_title')}</h5>
                        <div className="copy_content">
                            <Input value={copyValue} disabled />
                            <Button
                                className="refer_modal_btn"
                                onClick={() => {
                                    copyText(copyValue, sharedT, locale);
                                    try {
                                        gtm.push({
                                            event: 'uaEvent',
                                            event_name: 'referral_button',
                                            position: t('refer.pop_refer_title'),
                                            button_name: t('refer.pop_share_link_btn'),
                                        });
                                    } catch (e) {
                                        console.log(e);
                                    }
                                }}
                            >
                                {t('refer.pop_share_link_btn')}
                            </Button>
                        </div>
                        <p className="line">
                            <span>{t('refer.pop_other')}</span>
                        </p>
                    </section>
                    <section className="email-directly">
                        <h5 className="title">{t('refer.pop_email_directly_title')}</h5>
                        <div className="copy_content1">
                            <Input value={sendEmail} onChange={(e: any) => setSendEmail(e.target.value)} placeholder={t('refer.pop_input_placeholder')} />
                            <div className="send_email">
                                <Button
                                    className="refer_modal_btn"
                                    onClick={() => {
                                        handleSendMsg();
                                        try {
                                            gtm.push({
                                                event: 'uaEvent',
                                                event_name: 'referral_button',
                                                position: t('refer.pop_refer_title'),
                                                button_name: t('refer.pop_email_directly_btn'),
                                            });
                                        } catch (e) {
                                            console.log(e);
                                        }
                                    }}
                                    disabled={!reg.test(sendEmail)}
                                >
                                    {t('refer.pop_email_directly_btn')}
                                </Button>
                            </div>
                        </div>

                        <p className="line">
                            <span>{locale === 'jp' ? t('refer.pop_other1') : t('refer.pop_other')}</span>
                        </p>
                    </section>
                    <div className="tools_icon">
                        {isApp ? (
                            <Button className="refer_modal_btn" onClick={handleShareInfo}>
                                {t('refer.pop_share_conmon_share')}
                            </Button>
                        ) : (
                            <>
                                <EfLink
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${copyValue}`}
                                    dataLayer={{
                                        event: 'uaEvent',
                                        event_name: 'share',
                                        method: 'Facebook',
                                        item_id: 'Refer A Friend Now!',
                                    }}
                                    target="_blank"
                                >
                                    <FaceBookIconNew />
                                </EfLink>
                                <EfLink
                                    href={`https://twitter.com/intent/tweet?text=${shareText.twitter}`}
                                    dataLayer={{
                                        event: 'uaEvent',
                                        event_name: 'share',
                                        method: 'Twitter',
                                        item_id: 'Refer A Friend Now!',
                                    }}
                                    target="_blank"
                                >
                                    <TwitterIconNew />
                                </EfLink>
                            </>
                        )}
                    </div>
                    <p className="tips">{t('refer.share_footer_info')}</p>
                </div>
            </EfModal>
        </div>
    );
};

export default forwardRef<ReferHandle, ReferPorps>(Refer);
